import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Avatar,
  List,
  ListItemButton,
  Tooltip,
  Stack,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Collapse,
  Divider,
  Paper,
} from "@mui/material";
import {
  DirectionsBike,
  Person,
  ShoppingBag,
  Search,
  Close,
  Refresh,
  MyLocation,
  ChevronRight,
  ChevronLeft,
  FiberManualRecord,
  Wifi,
  WifiOff,
  LocationOn,
} from "@mui/icons-material";
import { io } from "socket.io-client";
import api from "../../api/api";
import { useSnackbar } from "../../utils/snackbar";

// ─── Map config ────────────────────────────────────────────────────────────────
const NAIROBI = [-1.2921, 36.8219];
const DARK_TILES =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const TILE_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

// ─── CSS keyframes injected once ───────────────────────────────────────────────
const ANIMATION_CSS = `
  @keyframes liveRing {
    0%   { transform: scale(1);   opacity: 0.8; }
    100% { transform: scale(3.5); opacity: 0; }
  }
  @keyframes livePulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.5; }
  }
  @keyframes rippleOut {
    0%   { transform: scale(0.5); opacity: 0.9; }
    100% { transform: scale(6);   opacity: 0; }
  }
  .rider-ping::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid #10b981;
    animation: liveRing 1.4s ease-out infinite;
    pointer-events: none;
  }
  .customer-ping::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    border: 1.5px solid #a855f7;
    animation: liveRing 2s ease-out infinite;
    pointer-events: none;
  }
  .leaflet-container { background: #0f172a !important; }
`;

// ─── Custom Leaflet icons ───────────────────────────────────────────────────────
const riderIcon = (online) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:38px;height:38px">
      ${online ? '<div class="rider-ping" style="position:absolute;inset:0;border-radius:50%"></div>' : ""}
      <div style="
        width:38px;height:38px;border-radius:50%;
        background:${online ? "linear-gradient(135deg,#059669,#10b981)" : "#1e293b"};
        border:2.5px solid ${online ? "#6ee7b7" : "#334155"};
        display:flex;align-items:center;justify-content:center;font-size:18px;
        box-shadow:${online ? "0 0 14px rgba(16,185,129,0.55)" : "none"};
      ">🏍️</div>
    </div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -22],
  });

const customerDot = (online) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:20px;height:20px">
      ${online ? '<div class="customer-ping" style="position:absolute;inset:0;border-radius:50%"></div>' : ""}
      <div style="
        width:20px;height:20px;border-radius:50%;
        background:${online ? "#a855f7" : "#334155"};
        border:2px solid ${online ? "#c084fc" : "#475569"};
        box-shadow:${online ? "0 0 10px rgba(168,85,247,0.6)" : "none"};
      "></div>
    </div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -14],
  });

// ─── Map auto-fit helper ────────────────────────────────────────────────────────
function AutoFit({ points }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (!fitted.current && points.length > 0) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
        fitted.current = true;
      }
    }
  }, [points, map]);

  return null;
}

// ─── Fly-to on panel click ──────────────────────────────────────────────────────
function FlyToTarget({ target }) {
  const map = useMap();
  const prev = useRef(null);

  useEffect(() => {
    if (!target) return;
    if (prev.current?.lat === target.lat && prev.current?.lng === target.lng) return;
    prev.current = target;
    map.flyTo([target.lat, target.lng], 16, { duration: 1.2, easeLinearity: 0.25 });
  }, [target, map]);

  return null;
}

// ─── Glass panel style helper ───────────────────────────────────────────────────
const glass = (extra = {}) => ({
  background: "rgba(15,23,42,0.82)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 3,
  color: "#f1f5f9",
  ...extra,
});

// ═══════════════════════════════════════════════════════════════════════════════
export default function LiveTracking() {
  const [riders, setRiders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [liveRiderLocs, setLiveRiderLocs] = useState({});
  const [liveCustLocs, setLiveCustLocs] = useState({});
  const [recentPings, setRecentPings] = useState([]); // [{lat,lng,id,ts}]
  const [pingCount, setPingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("customers");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [panelOpen, setPanelOpen] = useState(true);
  const [flyTarget, setFlyTarget] = useState(null);
  const socketRef = useRef(null);
  const { showAlert } = useSnackbar();

  // ── inject CSS once ──────────────────────────────────────────────────────────
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = ANIMATION_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ── data load ───────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [rRes, cRes] = await Promise.all([
        api.get("/api/admin/riders/with-orders"),
        api.get("/api/admin/customers/with-orders"),
      ]);
      setRiders(rRes.data.riders || []);
      setOrders(rRes.data.orders || []);
      setCustomers(cRes.data.customers || []);
    } catch {
      showAlert("Failed to load tracking data", "error");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── upsert helpers ───────────────────────────────────────────────────────────
  const upsertRider = useCallback((incoming) => {
    if (!incoming?._id) return;
    setRiders((prev) => {
      const idx = prev.findIndex((r) => r._id === incoming._id);
      const merged = { ...(idx !== -1 ? prev[idx] : {}), ...incoming, _updatedAt: Date.now() };
      if (idx === -1) return [...prev, merged];
      const copy = [...prev];
      copy[idx] = merged;
      return copy;
    });
  }, []);

  const upsertCustomer = useCallback((incoming) => {
    if (!incoming?._id) return;
    setCustomers((prev) => {
      const idx = prev.findIndex((c) => c._id === incoming._id);
      const merged = { ...(idx !== -1 ? prev[idx] : {}), ...incoming, _updatedAt: Date.now() };
      if (idx === -1) return [...prev, merged];
      const copy = [...prev];
      copy[idx] = merged;
      return copy;
    });
  }, []);

  // ── socket ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const base = (api.defaults?.baseURL || window.location.origin).replace(/\/+$/, "");
    const socket = io(base, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("rider:online",     ({ riderId, rider, lastSeen }) => upsertRider({ ...(rider || {}), _id: riderId, isOnline: true, lastSeen }));
    socket.on("rider:offline",    ({ riderId, rider, lastSeen }) => upsertRider({ ...(rider || {}), _id: riderId, isOnline: false, lastSeen }));
    socket.on("rider:heartbeat",  ({ riderId, rider, lastSeen }) => upsertRider({ ...(rider || {}), _id: riderId, isOnline: true, lastSeen }));

    socket.on("rider:location", ({ riderId, lat, lng }) => {
      if (!riderId || typeof lat !== "number") return;
      setLiveRiderLocs((p) => ({ ...p, [riderId]: { lat, lng, ts: Date.now() } }));
      upsertRider({ _id: riderId, isOnline: true });
    });

    socket.on("customer:location", ({ userId, customerId, lat, lng }) => {
      const id = userId || customerId;
      if (!id || typeof lat !== "number") return;
      setLiveCustLocs((p) => ({ ...p, [id]: { lat, lng, ts: Date.now() } }));
      upsertCustomer({ _id: id, currentLocation: { lat, lng }, isOnline: true });
      setPingCount((n) => n + 1);
      // add brief ripple marker
      const uid = `${id}_${Date.now()}`;
      setRecentPings((p) => [...p.slice(-30), { lat, lng, id: uid, ts: Date.now() }]);
      setTimeout(() => setRecentPings((p) => p.filter((x) => x.id !== uid)), 3000);
    });

    socket.on("customer:online",    ({ userId, customerId, lastSeen }) => upsertCustomer({ _id: userId || customerId, isOnline: true, lastSeen }));
    socket.on("customer:offline",   ({ userId, customerId, lastSeen }) => upsertCustomer({ _id: userId || customerId, isOnline: false, lastSeen }));
    socket.on("customer:heartbeat", ({ userId, lastSeen })             => upsertCustomer({ _id: userId, isOnline: true, lastSeen }));

    return () => socket.disconnect();
  }, [upsertRider, upsertCustomer]);

  // ── derived data ─────────────────────────────────────────────────────────────
  const getRiderLoc = (id) => {
    const live = liveRiderLocs[id];
    if (live) return live;
    const r = riders.find((x) => x._id === id);
    if (typeof r?.currentLocation?.lat === "number") return { ...r.currentLocation, ts: null };
    return null;
  };

  const getCustLoc = (id) => {
    const live = liveCustLocs[id];
    if (live) return live;
    const c = customers.find((x) => x._id === id);
    if (typeof c?.currentLocation?.lat === "number") return { ...c.currentLocation, ts: null };
    return null;
  };

  const allCustPoints = useMemo(() =>
    customers
      .map((c) => getCustLoc(c._id))
      .filter(Boolean),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [customers, liveCustLocs]);

  const riderPoints = useMemo(() =>
    riders
      .map((r) => ({ rider: r, loc: getRiderLoc(r._id) }))
      .filter((x) => x.loc),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [riders, liveRiderLocs]);

  const onlineRiders    = riders.filter((r) => r.isOnline).length;
  const onlineCustomers = customers.filter((c) => c.isOnline).length;
  const activeOrders    = orders.filter((o) => ["pending", "shipped", "in_transit"].includes(o.status)).length;

  const formatAgo = (ts) => {
    if (!ts) return "—";
    const s = Math.round((Date.now() - ts) / 1000);
    if (s < 10) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
  };

  // ── filtered list ────────────────────────────────────────────────────────────
  const listItems = useMemo(() => {
    const src = activeTab === "riders" ? riders : activeTab === "customers" ? customers : orders;
    return src.filter((item) => {
      const blob = JSON.stringify(item).toLowerCase();
      if (search && !blob.includes(search.toLowerCase())) return false;
      if (statusFilter === "online" && !item.isOnline) return false;
      if (statusFilter === "offline" && item.isOnline) return false;
      return true;
    });
  }, [riders, customers, orders, activeTab, search, statusFilter]);

  // ── auto fit all known points ─────────────────────────────────────────────────
  const allKnownPoints = useMemo(
    () => [...allCustPoints, ...riderPoints.map((x) => x.loc)],
    [allCustPoints, riderPoints]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Box
      sx={{
        position: "relative",
        height: "calc(100vh - 112px)",
        mx: -3,
        mt: -2,
        overflow: "hidden",
        borderRadius: 3,
      }}
    >
      {/* ── MAP ─────────────────────────────────────────────────────────────── */}
      <MapContainer
        center={NAIROBI}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer url={DARK_TILES} attribution={TILE_ATTR} />
        <AutoFit points={allKnownPoints} />
        <FlyToTarget target={flyTarget} />

        {/* Heatmap: outer glow rings per customer location */}
        {allCustPoints.map((loc, i) => (
          <React.Fragment key={`heat-${i}`}>
            <CircleMarker
              center={[loc.lat, loc.lng]}
              radius={55}
              pathOptions={{ color: "transparent", fillColor: "#7c3aed", fillOpacity: 0.025 }}
            />
            <CircleMarker
              center={[loc.lat, loc.lng]}
              radius={28}
              pathOptions={{ color: "transparent", fillColor: "#a855f7", fillOpacity: 0.07 }}
            />
          </React.Fragment>
        ))}

        {/* Precise customer markers */}
        {customers.map((c) => {
          const loc = getCustLoc(c._id);
          if (!loc) return null;
          return (
            <Marker key={c._id} position={[loc.lat, loc.lng]} icon={customerDot(c.isOnline)}>
              <Popup>
                <Box sx={{ minWidth: 160 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{c.name || "Customer"}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#64748b" }}>{c.phone || c.email}</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                    {loc.ts ? formatAgo(loc.ts) : "Last known location"}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          );
        })}

        {/* Rider markers */}
        {riderPoints.map(({ rider, loc }) => (
          <Marker key={rider._id} position={[loc.lat, loc.lng]} icon={riderIcon(rider.isOnline)}>
            <Popup>
              <Box sx={{ minWidth: 160 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{rider.name}</Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>{rider.phone}</Typography>
                <Chip
                  label={rider.isOnline ? "Online" : "Offline"}
                  size="small"
                  sx={{ mt: 0.5, fontSize: 11, bgcolor: rider.isOnline ? "#d1fae5" : "#fee2e2", color: rider.isOnline ? "#065f46" : "#991b1b" }}
                />
                {loc.ts && (
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                    {formatAgo(loc.ts)}
                  </Typography>
                )}
              </Box>
            </Popup>
          </Marker>
        ))}

        {/* Live ripple pings */}
        {recentPings.map((ping) => (
          <CircleMarker
            key={ping.id}
            center={[ping.lat, ping.lng]}
            radius={18}
            pathOptions={{ color: "#c084fc", fillColor: "transparent", opacity: 0.6, weight: 1.5 }}
          />
        ))}
      </MapContainer>

      {/* ── FLOATING TOP BAR ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          right: panelOpen ? 360 + 32 : 16,
          zIndex: 1000,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Title pill */}
        <Box sx={{ ...glass({ borderRadius: 99, px: 2.5, py: 1 }), display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981", animation: "livePulse 2s ease-in-out infinite" }} />
          <Typography sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5, color: "#f1f5f9" }}>
            LIVE TRACKING
          </Typography>
        </Box>

        {/* Stats chips */}
        <Box sx={{ ...glass({ borderRadius: 99, px: 2, py: 0.8 }), display: "flex", gap: 2, alignItems: "center" }}>
          <StatPill icon="🏍️" label="Riders" count={riders.length} online={onlineRiders} color="#10b981" />
          <Box sx={{ width: 1, height: 20, bgcolor: "rgba(255,255,255,0.1)" }} />
          <StatPill icon="👤" label="Customers" count={customers.length} online={onlineCustomers} color="#a855f7" />
          <Box sx={{ width: 1, height: 20, bgcolor: "rgba(255,255,255,0.1)" }} />
          <StatPill icon="📦" label="Active Orders" count={activeOrders} color="#f59e0b" />
        </Box>

        {/* Ping counter */}
        <Box sx={{ ...glass({ borderRadius: 99, px: 2, py: 0.8 }), display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#a855f7", animation: "livePulse 1.5s ease-in-out infinite" }} />
          <Typography sx={{ fontSize: 12, color: "#c084fc", fontWeight: 700 }}>
            {pingCount} pings
          </Typography>
        </Box>

        {/* Refresh */}
        <Tooltip title="Refresh data">
          <IconButton
            size="small"
            onClick={loadData}
            disabled={loading}
            sx={{ ...glass({ borderRadius: 99, width: 36, height: 36 }), color: "#94a3b8", "&:hover": { color: "#f1f5f9" } }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Toggle panel */}
        <Tooltip title={panelOpen ? "Hide panel" : "Show panel"}>
          <IconButton
            size="small"
            onClick={() => setPanelOpen((v) => !v)}
            sx={{ ...glass({ borderRadius: 99, width: 36, height: 36 }), color: "#94a3b8", "&:hover": { color: "#f1f5f9" }, ml: "auto" }}
          >
            {panelOpen ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── LEGEND (bottom-left) ─────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "absolute",
          bottom: 28,
          left: 16,
          zIndex: 1000,
          ...glass({ px: 2, py: 1.5 }),
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", mb: 1, letterSpacing: 1, textTransform: "uppercase" }}>
          Legend
        </Typography>
        <Stack spacing={0.8}>
          <LegendRow color="#10b981" label="Rider (online)" dot={false} emoji="🏍️" />
          <LegendRow color="#334155" label="Rider (offline)" dot={false} emoji="🏍️" dim />
          <LegendRow color="#a855f7" label="Customer (online)" />
          <LegendRow color="#334155" label="Customer (offline)" />
          <LegendRow color="#7c3aed" label="Density heatmap" heatmap />
        </Stack>
      </Box>

      {/* ── SIDE PANEL ───────────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          bottom: 16,
          width: 350,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          transform: panelOpen ? "translateX(0)" : "translateX(calc(100% + 24px))",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Tab + search */}
        <Box sx={{ ...glass(), p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            value={activeTab}
            onChange={(_, v) => v && setActiveTab(v)}
            size="small"
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                color: "#64748b",
                border: "1px solid rgba(255,255,255,0.08)",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                py: 0.7,
                "&.Mui-selected": { color: "#f1f5f9", bgcolor: "rgba(168,85,247,0.25)", borderColor: "rgba(168,85,247,0.4)" },
              },
            }}
          >
            <ToggleButton value="customers">
              <Person sx={{ fontSize: 16, mr: 0.5 }} /> Customers
            </ToggleButton>
            <ToggleButton value="riders">
              <DirectionsBike sx={{ fontSize: 16, mr: 0.5 }} /> Riders
            </ToggleButton>
            <ToggleButton value="orders">
              <ShoppingBag sx={{ fontSize: 16, mr: 0.5 }} /> Orders
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: "#475569" }} /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} sx={{ color: "#475569" }}><Close sx={{ fontSize: 14 }} /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "rgba(255,255,255,0.05)",
                color: "#f1f5f9",
                borderRadius: 2,
                "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                "&.Mui-focused fieldset": { borderColor: "#a855f7" },
              },
              "& .MuiInputBase-input::placeholder": { color: "#475569" },
            }}
          />

          {activeTab !== "orders" && (
            <ToggleButtonGroup
              exclusive
              value={statusFilter}
              onChange={(_, v) => v && setStatusFilter(v)}
              size="small"
              fullWidth
              sx={{
                "& .MuiToggleButton-root": {
                  color: "#64748b",
                  border: "1px solid rgba(255,255,255,0.08)",
                  textTransform: "none",
                  fontSize: 11,
                  py: 0.5,
                  "&.Mui-selected": { color: "#f1f5f9", bgcolor: "rgba(255,255,255,0.1)" },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="online">
                <FiberManualRecord sx={{ fontSize: 10, color: "#10b981", mr: 0.4 }} /> Online
              </ToggleButton>
              <ToggleButton value="offline">
                <FiberManualRecord sx={{ fontSize: 10, color: "#ef4444", mr: 0.4 }} /> Offline
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* List */}
        <Box sx={{ ...glass(), flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>
              {activeTab}
            </Typography>
            <Chip label={listItems.length} size="small" sx={{ height: 18, fontSize: 10, bgcolor: "rgba(255,255,255,0.08)", color: "#94a3b8" }} />
          </Box>

          <List sx={{ flex: 1, overflow: "auto", p: 1, "&::-webkit-scrollbar": { width: 4 }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.1)", borderRadius: 2 } }}>
            {listItems.map((item) => (
              <PanelItem
                key={item._id}
                item={item}
                type={activeTab}
                loc={
                  activeTab === "riders" ? getRiderLoc(item._id) :
                  activeTab === "customers" ? getCustLoc(item._id) : null
                }
                formatAgo={formatAgo}
                orders={orders}
                onFly={setFlyTarget}
              />
            ))}
            {listItems.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6, color: "#475569" }}>
                <LocationOn sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                <Typography sx={{ fontSize: 13 }}>No {activeTab} found</Typography>
              </Box>
            )}
          </List>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function StatPill({ icon, label, count, online, color, emoji }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Typography sx={{ fontSize: 14 }}>{icon}</Typography>
      <Box>
        <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1 }}>{label}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", lineHeight: 1.2 }}>{count}</Typography>
          {online != null && (
            <Typography sx={{ fontSize: 10, color, fontWeight: 700 }}>({online} live)</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function LegendRow({ color, label, dot = true, emoji, dim, heatmap }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {heatmap ? (
        <Box sx={{ width: 18, height: 10, borderRadius: 1, background: `linear-gradient(to right, transparent, ${color})`, opacity: 0.7 }} />
      ) : dot ? (
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, boxShadow: color !== "#334155" ? `0 0 6px ${color}` : "none", opacity: dim ? 0.4 : 1, flexShrink: 0 }} />
      ) : (
        <Typography sx={{ fontSize: 13, opacity: dim ? 0.4 : 1 }}>{emoji}</Typography>
      )}
      <Typography sx={{ fontSize: 11, color: dim ? "#334155" : "#94a3b8" }}>{label}</Typography>
    </Box>
  );
}

function PanelItem({ item, type, loc, formatAgo, orders, onFly }) {
  const isOnline = item.isOnline;
  const assignedOrders = type === "riders" ? orders.filter((o) => o.rider?._id === item._id).length : 0;
  const custOrders     = type === "customers" ? orders.filter((o) => o.user?._id === item._id).length : 0;

  const dotColor = type === "riders" ? "#10b981" : type === "customers" ? "#a855f7" : "#f59e0b";

  const statusColor = {
    completed: "#22c55e", pending: "#f59e0b", shipped: "#3b82f6", in_transit: "#10b981",
  }[item.status] || "#64748b";

  const handleClick = () => {
    if (loc) onFly({ lat: loc.lat, lng: loc.lng });
  };

  return (
    <ListItemButton
      onClick={handleClick}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        px: 1.5,
        py: 1,
        bgcolor: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.05)",
        cursor: loc ? "pointer" : "default",
        "&:hover": { bgcolor: loc ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.03)", borderColor: loc ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.05)" },
        transition: "all 0.15s",
      }}
    >
      {/* Avatar */}
      <Box sx={{ position: "relative", mr: 1.5, flexShrink: 0 }}>
        <Avatar
          sx={{
            width: 34, height: 34, fontSize: 13, fontWeight: 700,
            bgcolor: isOnline ? `${dotColor}22` : "rgba(255,255,255,0.06)",
            color: isOnline ? dotColor : "#475569",
            border: `1.5px solid ${isOnline ? `${dotColor}55` : "rgba(255,255,255,0.1)"}`,
          }}
        >
          {type === "riders" ? "🏍️" : type === "customers" ? (item.name?.charAt(0) || "C") : "#"}
        </Avatar>
        {type !== "orders" && (
          <Box
            sx={{
              position: "absolute", bottom: -1, right: -1,
              width: 9, height: 9, borderRadius: "50%",
              bgcolor: isOnline ? dotColor : "#374151",
              border: "1.5px solid #0f172a",
              boxShadow: isOnline ? `0 0 5px ${dotColor}` : "none",
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.3 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {type === "orders" ? `Order #${item._id?.slice(-6)}` : item.name || "Unknown"}
          </Typography>
          {type !== "orders" && (
            <Chip
              label={isOnline ? "live" : "off"}
              size="small"
              sx={{ height: 16, fontSize: 9, fontWeight: 700, ml: 0.5, flexShrink: 0, bgcolor: isOnline ? `${dotColor}22` : "rgba(255,255,255,0.06)", color: isOnline ? dotColor : "#475569" }}
            />
          )}
          {type === "orders" && (
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: statusColor, flexShrink: 0 }} />
          )}
        </Box>

        <Typography sx={{ fontSize: 11, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {type === "riders"    && (item.phone || "No phone")}
          {type === "customers" && (item.email || item.phone || "—")}
          {type === "orders"    && `${item.user?.name || "—"} • ${item.status}`}
        </Typography>

        {loc?.ts && (
          <Typography sx={{ fontSize: 10, color: "#334155", mt: 0.2 }}>
            📍 {formatAgo(loc.ts)}
          </Typography>
        )}

        {type === "riders" && assignedOrders > 0 && (
          <Typography sx={{ fontSize: 10, color: "#f59e0b", mt: 0.2 }}>
            {assignedOrders} order{assignedOrders > 1 ? "s" : ""} assigned
          </Typography>
        )}
        {type === "customers" && custOrders > 0 && (
          <Typography sx={{ fontSize: 10, color: "#64748b", mt: 0.2 }}>
            {custOrders} order{custOrders > 1 ? "s" : ""}
          </Typography>
        )}
      </Box>
    </ListItemButton>
  );
}
